import { useMemo, useState } from 'react'

import { Box, Typography, makeStyles } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { includes } from 'lodash-es'
import { Link } from 'react-router-dom'

import { COBRAND_ALIAS, SOURCES } from 'common/coalition-sources'
import OrLine from 'components/OrLine'
import SSOButtons from 'components/SSOButtons'
import Title from 'components/Title'

import SignInErrorConstants from '../../routes/SignIn/SignInErrorConstants'
import CredentialsForm from './CredentialsForm'
import { SignInFormProps, SignInFormValues } from './SignInForm.types'

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
    margin: theme.spacing(2, 0),
  },
}))

const DEFAULT_ROUTE = '/signup'
const useRegisterLink = (sourceAlias?: 'C' | 'CM', scid?: number) =>
  useMemo(() => {
    let link = DEFAULT_ROUTE
    if (sourceAlias && includes(SOURCES, COBRAND_ALIAS[sourceAlias])) {
      link = '/app/signup'
      if (scid) {
        link += `/${scid}`
      }
    }
    return link + window.location.search
  }, [sourceAlias, scid])

const SignInForm = ({
  onSubmit,
  onSuccess,
  onError,
  scid,
  source,
  errorCode,
  expiredError,
  passwordChangeRequired,
}: SignInFormProps) => {
  const classes = useStyles()
  const errorMessage = SignInErrorConstants(errorCode)
  const [error, setError] = useState(expiredError || errorMessage)
  const actualSource = source ? COBRAND_ALIAS[source] : undefined
  const registerLink = useRegisterLink(source, scid)

  const handleSubmit = async (values: SignInFormValues) => {
    await onSubmit(values)
    onSuccess?.()
  }

  const handleError = (errorMsg: string) => {
    setError(errorMsg)
    onError?.(errorMsg)
  }

  return (
    <>
      {passwordChangeRequired && (
        <Box mb={2}>
          <Alert severity="warning">
            Your password no longer meets your organization's requirements. Please{' '}
            <Link to="/forgot-password">reset your password</Link> to continue.
          </Alert>
        </Box>
      )}
      {error && !passwordChangeRequired && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Title title="Log in 👋" />
      <CredentialsForm
        onSubmit={handleSubmit}
        onError={handleError}
      />
      <Link
        to="/forgot-password"
        className={classes.link}
      >
        Forgot password?
      </Link>
      <OrLine />
      <SSOButtons
        actualSource={actualSource}
        scid={scid}
      />
      <div className="signup-wrapper">
        <Typography color="textSecondary">
          New to Scoir?{' '}
          <Link
            className={classes.link}
            to={registerLink}
          >
            Create Free Account
          </Link>
        </Typography>
      </div>
    </>
  )
}

export default SignInForm
